import {graphql} from 'gatsby'
import Cookies from 'js-cookie'
import React from 'react'
import styled from 'styled-components'

import {treasuryFormSubmitted} from '@/analytics/segment'
import {HubspotForm, isAllowedBgColor} from '@/components/hubspot-form'
import {Stack} from '@/components/stack'
import {Text} from '@/components/text'
import {paramCookiesMap} from '@/config/cookies'
import {useHubspotProperties} from '@/hooks/use-hubspot-properties'
import {container, Layout} from '@/styles/grid'
import {tokens} from '@/theme'
import {LatestLeadSourceCaptureType, LifecycleStages} from '@/types/marketing-campaigns'
import getEnvironmentField from '@/utils/get-environment-field'
import mq from '@/utils/media-query'

import type {HubspotHeroFormFragment} from '@/graphql-types'
import type {FormConfig} from '@/types/consents'

const TREASURY_FORMS_IDS = [
    'bc83c1cc-25bf-425b-a291-9966f76c2c5a', // staging
    '3d2cd5e9-f2f6-4d7e-9fbf-0f750b28f0a7', // prod
]

type Props = {
    content: HubspotHeroFormFragment
}

const HubspotHeroForm = ({content}: Props) => {
    const hubspotProperties = useHubspotProperties()

    const {form_production, form_staging, variant} = content.primary

    const formConfig = getEnvironmentField({
        production: form_production,
        staging: form_staging,
    })

    const isTreasuryForm = TREASURY_FORMS_IDS.includes(formConfig.formId)

    return (
        <MainSection $variant="two-asymmetrical" id="hubspot-form">
            <MobileHeader $variant="heading-base" as="h1">
                {content.primary.title}
            </MobileHeader>

            <Content $space={40}>
                <DesktopHeader $variant="heading-base" as="h1">
                    {content.primary.title}
                </DesktopHeader>
                <Stack $space={20}>
                    {content.items.map((item, index) => (
                        <ListWrapper key={index}>
                            <img style={{width: '50px'}} src={item.icon.url} alt="" />
                            <Text>{item.text}</Text>
                        </ListWrapper>
                    ))}
                </Stack>
            </Content>

            <HubspotForm
                content={{
                    title: content.primary.form_title,
                    submitButtonText: content.primary.form_submit_text,
                }}
                hubspotProperties={{
                    ...hubspotProperties,
                    lifecyclestage: LifecycleStages.Subscriber,
                    lead_source_latest_capture_type: LatestLeadSourceCaptureType.FreeDemo,
                }}
                formConfig={formConfig as FormConfig}
                variant={isAllowedBgColor(variant) ? variant : 'default'}
                redirectTo={content.primary.redirect_to.url}
                onSubmitSuccess={(state) => {
                    if (isTreasuryForm) {
                        treasuryFormSubmitted({
                            email: state.email,
                            gclid: Cookies.get(paramCookiesMap.googleClickIdentifier) || '',
                        })
                    }
                }}
            />
        </MainSection>
    )
}

const MainSection = styled(Layout)`
    align-items: start;
    padding-top: ${tokens.spacing40};
    padding-bottom: ${tokens.spacing40};

    ${container}
`

const Content = styled(Stack)`
    padding-top: ${({theme}) => theme.spacings.s20};
    position: sticky;
    top: 85px;

    ${mq.min('sm')} {
        padding-top: 100px;
    }

    ${mq.max('sm')} {
        order: 2;
    }
`

const MobileHeader = styled(Text)`
    ${mq.min('sm')} {
        display: none;
    }
`

const DesktopHeader = styled(Text)`
    ${mq.max('sm')} {
        display: none;
    }
`

const ListWrapper = styled.li`
    display: grid;
    column-gap: ${({theme}) => theme.spacings.s20};
    grid-auto-flow: column;
    justify-content: start;
    align-items: center;
`

export const hubspotHeroFormFragment = graphql`
    fragment HubspotHeroForm on PrismicSlicePageDataBodyHubspotHeroForm {
        __typename
        primary {
            title
            variant
            form_title
            form_submit_text
            form_production {
                formId
                consents {
                    type
                    communicationConsents {
                        id
                        required
                    }
                }
            }
            form_staging {
                formId
                consents {
                    type
                    communicationConsents {
                        id
                        required
                    }
                }
            }
            redirect_to {
                ...PrismicLink
            }
        }
        items {
            icon {
                alt
                url
                copyright
                dimensions {
                    height
                    width
                }
            }
            text
        }
    }
`

export default HubspotHeroForm
